// @flow
import api from '../api/review';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';
import { OPEN_REVIEWS_MODAL } from './modal';

/* Action Types */
export const FETCH_COMMENTS_FAIL = 'la/domain/fetchSellerComments/FAIL';
export const FETCH_COMMENTS_REQUEST = 'la/domain/fetchSellerComments/REQUEST';
export const FETCH_COMMENTS_SUCCESS = 'la/domain/fetchSellerComments/SUCCESS';

export const FETCH_PERCENTILES_FAIL = 'la/domain/fetchSellerPercentiles/FAIL';
export const FETCH_PERCENTILES_REQUEST = 'la/domain/fetchSellerPercentiles/REQUEST';
export const FETCH_PERCENTILES_SUCCESS = 'la/domain/fetchSellerPercentiles/SUCCESS';

export const FETCH_RATINGS_FAIL = 'la/domain/fetchSellerRatings/FAIL';
export const FETCH_RATINGS_REQUEST = 'la/domain/fetchSellerRatings/REQUEST';
export const FETCH_RATINGS_SUCCESS = 'la/domain/fetchSellerRatings/SUCCESS';

const REDUX_STORE_TIME = ms('30m');

//reducer
const DEFAULT_STATE = {
    activeSeller: 0,
    byId: {},
    commentsById: {},
    commentsLoaded: {},
    commentsLoading: false,
    loaded: {},
    loading: false,
    percentilesById: {},
    percentilesLoaded: {},
    percentilesLoading: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case FETCH_COMMENTS_FAIL:
            return {
                ...state,
                commentsLoading: false,
            };
        case FETCH_RATINGS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case FETCH_PERCENTILES_FAIL:
            return {
                ...state,
                percentilesLoading: false,
            };
        case FETCH_COMMENTS_REQUEST:
            return {
                ...state,
                commentsLoading: true,
            };
        case FETCH_RATINGS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case FETCH_PERCENTILES_REQUEST:
            return {
                ...state,
                percentilesLoading: true,
            };
        case FETCH_COMMENTS_SUCCESS:
            const existingComments = cloneDeep(state.commentsById) || {};
            const existingCommentsLoaded = cloneDeep(state.commentsLoaded) || {};
            const NewCommentsLoaded = {};
            NewCommentsLoaded[action.meta.sellerId] = action.meta.actionTime;
            return {
                ...state,
                commentsById: Object.assign(existingComments, action.payload),
                commentsLoaded: Object.assign(existingCommentsLoaded, NewCommentsLoaded),
                commentsLoading: false,
            };
        case FETCH_RATINGS_SUCCESS:
            const existing = cloneDeep(state.byId) || {};
            const existingLoaded = cloneDeep(state.loaded) || {};
            const NewRatingLoaded = {};
            NewRatingLoaded[action.meta.sellerId] = action.meta.actionTime;
            return {
                ...state,
                byId: Object.assign(existing, action.payload),
                loaded: Object.assign(existingLoaded, NewRatingLoaded),
                loading: false,
            };
        case FETCH_PERCENTILES_SUCCESS:
            const existingPercentiles = cloneDeep(state.percentilesById) || {};
            const existingPercentilesLoaded = cloneDeep(state.percentilesLoaded) || {};
            const NewPercentile = {};
            NewPercentile[action.meta.sellerId] = action.meta.actionTime;
            return {
                ...state,
                percentilesById: Object.assign(existingPercentiles, action.payload),
                percentilesLoaded: Object.assign(existingPercentilesLoaded, NewPercentile),
                percentilesLoading: false,
            };
        case OPEN_REVIEWS_MODAL:
            return {
                ...state,
                activeSeller: action.payload,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.sellerRatings;
const idSelector = (state: GlobalState, id) => id;

export const byIdSelector = createSelector(stateSelector, state => state.byId || {});

export const getActiveSeller = createSelector(stateSelector, state => state.activeSeller || 0);

export const commentsByIdSelector = createSelector(stateSelector, state => state.commentsById || {});

export const percentilesByIdSelector = createSelector(stateSelector, state => state.percentilesById || {});

export const commentsLoadedSelector = createSelector(stateSelector, state => state.commentsLoaded || {});

export const loadedSelector = createSelector(stateSelector, state => state.loaded);

export const percentilesLoadedSelector = createSelector(stateSelector, state => state.percentilesLoaded || {});

export const getCommentsLoading = createSelector(stateSelector, state => state.commentsLoading);

export const getIsLoading = createSelector(stateSelector, state => state.loading);

export const getPercentilesLoading = createSelector(stateSelector, state => state.percentilesLoading);

export const getLoadedCommentsById = createSelector([commentsLoadedSelector, idSelector], (loaded, id) => {
    return loaded[id] || 0;
});

export const getCommentsById = createSelector([commentsByIdSelector, idSelector], (byId, id) => {
    return byId[id] || {};
});

export const getLoadedSellerRatingById = createSelector([loadedSelector, idSelector], (loaded, id) => {
    return loaded[id] || 0;
});

export const getSellerRatingById = createSelector([byIdSelector, idSelector], (byId, id) => {
    return byId[id] || {};
});

export const getLoadedPercentilesById = createSelector([percentilesLoadedSelector, idSelector], (byId, id) => {
    return byId[id] || 0;
});

export const getSellerPercentilesById = createSelector([percentilesByIdSelector, idSelector], (percentilesById, id) => {
    return percentilesById[id] || {};
});

const shouldFetchSellerCommentsByIdIfNeeded = (state, sellerId) => {
    const existingRating = getLoadedCommentsById(state, sellerId);
    const isLoading = getCommentsLoading(state);
    if (isLoading) return false;
    if (!existingRating) {
        return true;
    } else {
        const diff = Date.now() - existingRating;
        return diff > REDUX_STORE_TIME;
    }
};

const shouldFetchSellerRatingsByIdIfNeeded = (state, sellerId) => {
    const existingRating = getLoadedSellerRatingById(state, sellerId);
    const isLoading = getIsLoading(state);
    if (isLoading) return false;
    if (!existingRating) {
        return true;
    } else {
        const diff = Date.now() - existingRating;
        return diff > REDUX_STORE_TIME;
    }
};

const shouldFetchSellerPercentilesByIdIfNeeded = (state, sellerId) => {
    const existingPercentiles = getLoadedPercentilesById(state, sellerId);
    const isLoading = getPercentilesLoading(state);
    if (isLoading) return false;
    if (!existingPercentiles) {
        return true;
    } else {
        const diff = Date.now() - existingPercentiles;
        return diff > REDUX_STORE_TIME;
    }
};

/* ACTION CREATORS */
export const loadSellerRatingsById = ({ filter = '', page = 1, pageSize = 8, sort, sellerId }: any) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: FETCH_RATINGS_REQUEST,
        });

        const response = await api.fetchHouseReviewData({
            authToken,
            deployment,
            filter,
            houseId: sellerId,
            page,
            pageSize,
            sort,
        });
        dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response,
            type: FETCH_RATINGS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: FETCH_RATINGS_FAIL,
        });
    }
};

const loadSellerPercentilesById = sellerId => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: FETCH_PERCENTILES_REQUEST,
        });
        const response = await api.fetchHousePercentiles({ authToken, deployment, houseId: sellerId });
        dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response,
            type: FETCH_PERCENTILES_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: FETCH_PERCENTILES_FAIL,
        });
    }
};

const loadSellerCommentsById = sellerId => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: FETCH_COMMENTS_REQUEST,
        });
        const response = await api.fetchHouseComments({ authToken, deployment, houseId: sellerId });
        dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response,
            type: FETCH_COMMENTS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: FETCH_COMMENTS_FAIL,
        });
    }
};

export const fetchSellerRatingsByIdIfNeeded = ({ filter = '', page = 1, pageSize = 8, sort, sellerId }: any) => (
    dispatch: Function,
    getState: Function
) => {
    if (shouldFetchSellerRatingsByIdIfNeeded(getState(), sellerId)) {
        return dispatch(loadSellerRatingsById({ filter, page, pageSize, sellerId, sort }));
    }
    return Promise.resolve();
};

export const fetchSellerPercentilesByIdIfNeeded = (sellerId: number) => (dispatch: Function, getState: Function) => {
    if (shouldFetchSellerPercentilesByIdIfNeeded(getState(), sellerId)) {
        return dispatch(loadSellerPercentilesById(sellerId));
    }
    return Promise.resolve();
};

export const fetchSellerCommentsByIdIfNeeded = (sellerId: number) => (dispatch: Function, getState: Function) => {
    if (shouldFetchSellerCommentsByIdIfNeeded(getState(), sellerId)) {
        return dispatch(loadSellerCommentsById(sellerId));
    }
    return Promise.resolve();
};
